// AdminReposPage.jsx — 第八十六批：管理员浏览「项目源码类 git 仓库」（只读）。
//
// 需求方裁：「粗略查看仓库所含文件，文本类文件支持直接展示，所有的二进制只支持
// 下载即可，提交历史可以仅精确到分支」。
//
// 三条收窄都来自"粗略"这两个字：
//   · **只到分支**——分支选择器 + 文件树，不碰提交历史。"看看仓里有什么"与
//     "审计谁改了什么"是两件事，后者要的是另一套面。
//   · **文本直展、二进制只给下载**——判定在后端（gitea_is_text_path 的白名单），
//     前端不重判一遍：重判就是把"什么算文本"放到两处，而两处迟早会漂开。
//   · **超限文本也退化为下载**——截断的文本看起来像完整的，那比不给更糟。
import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Breadcrumbs, Button, Chip, CircularProgress, Link as MuiLink,
  MenuItem, Paper, Stack, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import api from '../api/client.mjs';
import { errKey } from '../api/errText.mjs';
import AppSnackbar, { useNotify } from '../components/AppSnackbar.jsx';
import AdminSubnav from '../components/AdminSubnav.jsx';
import DataTable from '../components/DataTable.jsx';
import HelpLink from '../components/HelpLink.jsx';
import { useT } from '../i18n/index.mjs';
import { breadcrumb, parentPath, viewOf, fmtBytes, repoCloneUrl, repoCloneHint, branchPick }
  from './repoBrowse.mjs';

export default function AdminReposPage() {
  const t = useT();
  const { snack, notify, clearSnack } = useNotify();

  const [repos, setRepos] = useState({ status: 'loading', list: [], provisioned: false,
                                       publicBaseUrl: '' });
  const [repo, setRepo] = useState('');
  const [branches, setBranches] = useState([]);
  // SB-159 复审：分支清单的结局（被截断／main、master 不在清单里）——branchPick 给的那几句。
  const [branchNotes, setBranchNotes] = useState([]);
  const [ref, setRef] = useState('');
  const [path, setPath] = useState('');
  const [node, setNode] = useState({ status: 'idle', payload: null, error: '' });

  // ── 仓列表 ────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const d = await api.adminRepos();
        const list = Array.isArray(d?.repos) ? d.repos : [];
        setRepos({ status: 'ready', list, provisioned: Boolean(d?.provisioned),
                   publicBaseUrl: String(d?.publicBaseUrl || '') });
        if (list.length === 1) setRepo(list[0]);   // 只有一个仓就不必让人选
      } catch (e) {
        setRepos({ status: 'error', list: [], provisioned: false, publicBaseUrl: '' });
        notify(t(errKey(e, { fallback: 'admin.repos.error' })), 'error');
      }
    })();
  }, [notify, t]);

  // ── 分支（选仓即拉）────────────────────────────────────────────────────
  useEffect(() => {
    if (!repo) return;
    setBranchNotes([]);
    (async () => {
      try {
        const d = await api.adminRepoBranches(repo);
        // 默认分支：优先 main / master，否则第一个（判据在 repoBrowse.mjs 的 branchPick）。
        // SB-159 复审：清单被截断（truncated:true）要说出来；main／master 不在清单里时也要说——
        // 而不是静静打开另一个分支，让人以为那就是默认分支。
        const bp = branchPick(d);
        setBranches(bp.branches);
        setRef(bp.pick);
        setBranchNotes(bp.notes);
        setPath('');
      } catch (e) {
        setBranches([]);
        setBranchNotes([]);
        notify(t(errKey(e, { fallback: 'admin.repos.error' })), 'error');
      }
    })();
  }, [repo, notify, t]);

  // ── 内容（仓/分支/路径任一变即拉）──────────────────────────────────────
  const loadNode = useCallback(async () => {
    if (!repo || !ref) { setNode({ status: 'idle', payload: null, error: '' }); return; }
    setNode((s) => ({ ...s, status: 'loading' }));
    try {
      const d = await api.adminRepoContents({ repo, path, ref });
      setNode({ status: 'ready', payload: d, error: '' });
    } catch (e) {
      setNode({ status: 'error', payload: null,
                error: t(errKey(e, { byStatus: { 404: 'admin.repos.notFound' },
                                     fallback: 'admin.repos.error' })) });
    }
  }, [repo, ref, path, t]);
  useEffect(() => { loadNode(); }, [loadNode]);

  const v = node.payload ? viewOf(node.payload) : null;
  const crumbs = breadcrumb(path, t('admin.repos.root'));
  const up = parentPath(path);

  return (
    <Box>
      <AdminSubnav />
      <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
        {t('admin.repos.title')}
        <HelpLink section="admin" />
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('admin.repos.subtitle')}
      </Typography>

      {repos.status === 'ready' && !repos.provisioned ? (
        // 桥没供给时**说得出是哪一件没配**，而不是让每次点击各自 503 一次让人猜。
        <Alert severity="warning" sx={{ mb: 2 }}>{t('admin.repos.notProvisioned')}</Alert>
      ) : null}

      {/* 第九十批：当前选中仓的**公网克隆地址与凭据用法**。
          此前这两件事只存在于发凭据那个人的脑子里——他要自己记得路径前缀是 `/git/`、
          记得域名随 stage 变、记得凭据走 Basic。一件只存在于某个人脑子里的操作步骤，
          就是下一次由别人做时会做错的那一步。 */}
      {(() => {
        const url = repos.status === 'ready' ? repoCloneUrl(repos.publicBaseUrl, repo) : null;
        if (!repo || repos.status !== 'ready') return null;
        return (
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              {t('admin.repos.clone.title')}
            </Typography>
            {url ? (
              <>
                <Box component="code" sx={{ display: 'block', fontFamily: 'monospace',
                  fontSize: 13, wordBreak: 'break-all', mb: 1 }}>
                  {url}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {t('admin.repos.clone.howto')}
                </Typography>
                <Box component="code" sx={{ display: 'block', fontFamily: 'monospace',
                  fontSize: 12, wordBreak: 'break-all', mt: 0.5, color: 'text.secondary' }}>
                  {repoCloneHint(url)}
                </Box>
              </>
            ) : (
              // base 没配时**说清楚是哪一件没配**，而不是给半条地址——
              // 半条地址看起来能复制，粘出去却是死链。
              <Typography variant="body2" color="text.secondary">
                {t('admin.repos.clone.noBase')}
              </Typography>
            )}
          </Paper>
        );
      })()}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          select size="small" label={t('admin.repos.repo')} value={repo}
          onChange={(e) => { setRepo(e.target.value); setPath(''); }}
          disabled={repos.status !== 'ready' || repos.list.length === 0}
          sx={{ minWidth: 260 }}
        >
          {repos.list.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
        </TextField>
        <TextField
          select size="small" label={t('admin.repos.branch')} value={ref}
          onChange={(e) => { setRef(e.target.value); setPath(''); }}
          disabled={branches.length === 0}
          sx={{ minWidth: 220 }}
          helperText={t('admin.repos.branchOnly')}
        >
          {branches.map((b) => (
            <MenuItem key={b.name} value={b.name}>
              {b.name}
              {b.commitId ? (
                <Typography component="span" variant="caption" color="text.secondary"
                            sx={{ ml: 1, fontFamily: 'ui-monospace, monospace' }}>
                  {String(b.commitId).slice(0, 7)}
                </Typography>
              ) : null}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {/* SB-159 复审：分支清单被截断、或 main／master 不在清单里——看得见的一句，不让下拉冒充全部。 */}
      {branchNotes.map((n) => (
        <Alert key={n.key} severity={n.severity} sx={{ mb: 2 }} data-branch-note={n.key}>
          {t(n.key, n.params)}
        </Alert>
      ))}

      {repo && ref ? (
        <Breadcrumbs sx={{ mb: 1 }}>
          {crumbs.map((c, i) => (
            i === crumbs.length - 1
              ? <Typography key={c.path} color="text.primary"
                            sx={{ fontFamily: 'ui-monospace, monospace' }}>{c.name}</Typography>
              : <MuiLink key={c.path} component="button" underline="hover"
                         onClick={() => setPath(c.path)}
                         sx={{ fontFamily: 'ui-monospace, monospace' }}>{c.name}</MuiLink>
          ))}
        </Breadcrumbs>
      ) : null}

      {node.status === 'loading' ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : node.status === 'error' ? (
        <Box><Alert severity="error" sx={{ mb: 1 }}>{node.error}</Alert>
          <Button size="small" onClick={loadNode}>{t('common.retry')}</Button></Box>
      ) : v?.view === 'dir' || v?.view === 'empty-dir' ? (
        <DataTable minWidth={400}>
            <TableHead>
              <TableRow>
                <TableCell>{t('admin.repos.name')}</TableCell>
                <TableCell align="right">{t('admin.repos.size')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {up !== null ? (
                <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setPath(up)}>
                  <TableCell sx={{ fontFamily: 'ui-monospace, monospace' }}>../</TableCell>
                  <TableCell />
                </TableRow>
              ) : null}
              {v.view === 'empty-dir' ? (
                // 空目录要有话说，而不是一片空白让人以为页面坏了。
                <TableRow><TableCell colSpan={2}>
                  <Typography variant="body2" color="text.secondary">
                    {t('admin.repos.emptyDir')}
                  </Typography>
                </TableCell></TableRow>
              ) : v.entries.map((e) => (
                <TableRow key={e.path} hover sx={{ cursor: 'pointer' }}
                          onClick={() => setPath(e.path)}>
                  <TableCell sx={{ fontFamily: 'ui-monospace, monospace' }}>
                    {e.type === 'dir' ? `${e.name}/` : e.name}
                  </TableCell>
                  <TableCell align="right">
                    {e.type === 'dir' ? '—' : fmtBytes(e.size)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
        </DataTable>
      ) : v?.view === 'text' ? (
        <Paper variant="outlined" sx={{ p: 1.5, overflowX: 'auto' }}>
          <Box component="pre" sx={{
            m: 0, fontFamily: 'ui-monospace, monospace', fontSize: '0.8rem',
            whiteSpace: 'pre', tabSize: 4,
          }}>{v.text}</Box>
        </Paper>
      ) : v?.view === 'download' ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack sx={{ alignItems: 'flex-start' }} spacing={1.5}>
            <Chip size="small"
                  label={v.reason === 'binary' ? t('admin.repos.binary') : t('admin.repos.tooLarge')} />
            <Typography variant="body2" color="text.secondary">
              {v.reason === 'binary'
                ? t('admin.repos.binaryHint')
                : t('admin.repos.tooLargeHint', { size: fmtBytes(v.size),
                                                  limit: fmtBytes(v.limit) })}
            </Typography>
            {v.canDownload ? (
              <Button size="small" variant="outlined" component="a"
                      href={api.adminRepoRawUrl({ repo, path: v.path, ref })}
                      target="_blank" rel="noreferrer">
                {t('admin.repos.download')}
              </Button>
            ) : null}
          </Stack>
        </Paper>
      ) : null}

      <AppSnackbar snack={snack} onClose={clearSnack} />
    </Box>
  );
}
